import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { MagnifyingGlassIcon } from "@phosphor-icons/react";
import type { ChoiceFilter } from "./types";

/** The picker's toolbar: the search box, the inline filters, then the chips. */
export function ChoiceFilters({
  search,
  onSearchChange,
  searchPlaceholder,
  filters,
}: {
  search: string;
  onSearchChange: (search: string) => void;
  searchPlaceholder: string;
  filters: ChoiceFilter[];
}) {
  return (
    <div className="space-y-3 border-t px-6 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-40 flex-1">
          <MagnifyingGlassIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
          <Input
            type="search"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            className="pl-8"
          />
        </div>
        {filters.map((filter) =>
          filter.kind === "select" ? (
            <Select
              key={filter.id}
              value={filter.value}
              onValueChange={filter.onChange}
            >
              <SelectTrigger
                className="w-auto min-w-40"
                aria-label={filter.label}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {filter.options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : filter.kind === "segmented" ? (
            <div
              key={filter.id}
              role="group"
              aria-label={filter.label}
              // A long row of options scrolls rather than wrapping its labels.
              className="bg-muted flex max-w-full overflow-x-auto rounded-md p-0.5"
            >
              {filter.options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={filter.value === option.value}
                  onClick={() => filter.onChange(option.value)}
                  className={cn(
                    "focus-visible:ring-ring shrink-0 rounded-sm px-2.5 py-1 text-xs font-medium whitespace-nowrap outline-none focus-visible:ring-2",
                    filter.value === option.value
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          ) : null,
        )}
      </div>

      {filters.map(
        (filter) =>
          filter.kind === "chips" &&
          filter.options.length > 0 && (
            <div
              key={filter.id}
              role="group"
              aria-label={filter.label}
              className="flex flex-wrap items-center gap-1.5"
            >
              <span className="text-muted-foreground mr-1 text-xs font-medium">
                {filter.label}
              </span>
              {filter.options.map((option) => {
                const active = filter.value.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() =>
                      filter.onChange(
                        active
                          ? filter.value.filter((v) => v !== option.value)
                          : [...filter.value, option.value],
                      )
                    }
                    className={cn(
                      "focus-visible:ring-ring rounded-full border px-2.5 py-0.5 text-xs outline-none focus-visible:ring-2",
                      active
                        ? "border-primary bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground hover:border-foreground/30",
                    )}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          ),
      )}
    </div>
  );
}
